import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import Header from '@/components/Header'
import { Badge } from '@/components/ui/badge'
import { toast } from 'sonner'
import {
  ArrowRight,
  BarChart3,
  Upload,
  ScanLine,
  CreditCard,
  Briefcase,
  CheckCircle2,
  XCircle,
  ChevronDown,
  ChevronUp,
  Car,
  FileText,
  Trash2,
  Loader2,
} from 'lucide-react'
import { API } from '@/lib/api'
import { httpRequest, HttpError } from '@/lib/http'

const STEPS = [
  { key: 'uploaded', label: 'Document envoye', icon: Upload },
  { key: 'analyzed', label: 'Analyse terminee', icon: ScanLine },
  { key: 'confirmed', label: 'Demande confirmee', icon: CreditCard },
  { key: 'in_progress', label: 'Traitement en cours', icon: Briefcase },
  { key: 'completed', label: 'Dossier finalise', icon: CheckCircle2 },
]

function resolveCurrentStep(analysis) {
  const ps = analysis.payment_status || 'none'
  if (ps === 'completed') return 4
  if (ps === 'in_progress') return 3
  if (ps === 'accepted') return 2
  if (ps === 'cancelled') return -1
  if (analysis.result) return 1
  return 0
}

function StepperTimeline({ currentStep, cancelled }) {
  return (
    <div className="flex items-center gap-0 w-full">
      {STEPS.map((step, i) => {
        const Icon = step.icon
        const done = !cancelled && currentStep >= i
        const active = !cancelled && currentStep === i
        const isCancelled = cancelled && i === 2

        let dotClass = 'bg-[#2c2c2e] border-white/[0.08] text-[#f5f5f7]/30'
        if (isCancelled) dotClass = 'bg-red-500/15 border-red-500/40 text-red-400'
        else if (done) dotClass = 'bg-green-500/15 border-green-500/40 text-green-400'
        else if (active) dotClass = 'bg-green-500/15 border-green-500/40 text-green-400 ring-2 ring-green-500/20'

        let labelClass = 'text-[#f5f5f7]/25'
        if (isCancelled) labelClass = 'text-red-400'
        else if (done) labelClass = 'text-green-400'
        else if (active) labelClass = 'text-green-400'

        let lineClass = 'bg-white/[0.06]'
        if (!cancelled && currentStep >= i + 1) lineClass = 'bg-green-500/40'

        return (
          <React.Fragment key={step.key}>
            <div className="flex flex-col items-center gap-1.5 min-w-0 flex-shrink-0">
              <div className={`w-8 h-8 rounded-full border flex items-center justify-center transition-all duration-300 ${dotClass}`}>
                {isCancelled ? <XCircle size={14} /> : <Icon size={14} />}
              </div>
              <span className={`text-[10px] font-semibold tracking-wide text-center leading-tight max-w-[72px] ${labelClass}`}>
                {isCancelled ? 'Annule' : step.label}
              </span>
            </div>
            {i < STEPS.length - 1 && (
              <div className={`flex-1 h-[2px] rounded-full min-w-[12px] mx-0.5 mt-[-18px] ${lineClass}`} />
            )}
          </React.Fragment>
        )
      })}
    </div>
  )
}

function DossierCard({ analysis, formatCurrency, formatDate, onNavigate, onDelete }) {
  const [expanded, setExpanded] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const r = analysis.result || {}
  const savings = r.savings_analysis || {}
  const vehicle = r.vehicle || {}
  const contract = r.contract_details || {}
  const commission = r.commission || {}
  const ps = analysis.payment_status || 'none'
  const cancelled = ps === 'cancelled'
  const currentStep = resolveCurrentStep(analysis)

  const statusConfig = {
    none: { label: 'En attente', color: 'bg-[#2c2c2e] text-[#f5f5f7]/50 border-white/[0.08]' },
    pending: { label: 'A confirmer', color: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/30' },
    accepted: { label: 'Confirmee', color: 'bg-green-500/10 text-green-400 border-green-500/30' },
    in_progress: { label: 'En cours', color: 'bg-blue-500/10 text-blue-400 border-blue-500/30' },
    completed: { label: 'Terminee', color: 'bg-green-500/10 text-green-400 border-green-500/30' },
    cancelled: { label: 'Annulee', color: 'bg-red-950/40 text-red-400 border-red-500/30' },
  }

  const badgeKey = ps === 'none' && analysis.has_savings ? 'pending' : ps
  const st = statusConfig[badgeKey] || statusConfig.none

  const events = []
  if (analysis.created_at) {
    events.push({ date: analysis.created_at, label: 'Document envoye et analyse lancee' })
  }
  if (analysis.accepted_at) {
    events.push({ date: analysis.accepted_at, label: analysis.promo_code ? `Demande confirmee (code ${analysis.promo_code})` : 'Demande confirmee' })
  }
  if (analysis.stripe_paid_at) {
    events.push({ date: analysis.stripe_paid_at, label: 'Paiement Stripe recu' })
  }
  events.sort((a, b) => new Date(a.date) - new Date(b.date))

  return (
    <div className="bg-[#1c1c1e] border border-white/[0.08] rounded-2xl overflow-hidden transition-all duration-[280ms]">
      {/* Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full text-left px-6 py-5 flex items-start gap-4 hover:bg-white/[0.02] transition-colors duration-[180ms]"
      >
        <div className="w-10 h-10 rounded-xl bg-white/[0.04] flex items-center justify-center shrink-0 mt-0.5">
          <FileText size={18} className="text-[#f5f5f7]/40" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <p className="text-sm font-bold text-[#f5f5f7] truncate">
              {analysis.document?.original_filename || 'Document'}
            </p>
            <Badge className={`rounded-lg text-[10px] shrink-0 ${st.color}`}>{st.label}</Badge>
          </div>
          <div className="flex items-center gap-3 text-xs text-[#f5f5f7]/35">
            {vehicle.make && (
              <span className="flex items-center gap-1">
                <Car size={11} />
                {vehicle.make} {vehicle.model || ''}
              </span>
            )}
            <span>{r.document_type || ''}</span>
            <span>{formatDate(analysis.created_at)}</span>
          </div>
          {savings.total_savings > 0 && (
            <p className="text-sm font-black text-green-400 mt-1.5">
              Economie: {formatCurrency(savings.total_savings)}
            </p>
          )}
        </div>
        <div className="flex items-center gap-3 shrink-0">
          {expanded ? (
            <ChevronUp size={16} className="text-[#f5f5f7]/30" />
          ) : (
            <ChevronDown size={16} className="text-[#f5f5f7]/30" />
          )}
        </div>
      </button>

      {/* Expanded content */}
      {expanded && (
        <div className="border-t border-white/[0.06] px-6 py-5 space-y-6">
          {/* Stepper */}
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#f5f5f7]/30 mb-4">
              Avancement du dossier
            </p>
            <StepperTimeline currentStep={currentStep} cancelled={cancelled} />
          </div>

          {/* Financial summary */}
          {savings.total_savings > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="bg-black/30 rounded-xl px-4 py-3 border border-white/[0.04]">
                <p className="text-[10px] uppercase tracking-[0.1em] font-semibold text-[#f5f5f7]/30 mb-1">Mensualite actuelle</p>
                <p className="text-base font-black text-[#f5f5f7]/70 font-heading">{formatCurrency(savings.current_monthly)}</p>
              </div>
              <div className="bg-black/30 rounded-xl px-4 py-3 border border-white/[0.04]">
                <p className="text-[10px] uppercase tracking-[0.1em] font-semibold text-green-400/60 mb-1">Mensualite proposee</p>
                <p className="text-base font-black text-green-400 font-heading">{formatCurrency(savings.proposed_monthly)}</p>
              </div>
              <div className="bg-black/30 rounded-xl px-4 py-3 border border-white/[0.04]">
                <p className="text-[10px] uppercase tracking-[0.1em] font-semibold text-[#f5f5f7]/30 mb-1">Economie totale</p>
                <p className="text-base font-black text-green-400 font-heading">{formatCurrency(savings.total_savings)}</p>
              </div>
              <div className="bg-black/30 rounded-xl px-4 py-3 border border-white/[0.04]">
                <p className="text-[10px] uppercase tracking-[0.1em] font-semibold text-[#f5f5f7]/30 mb-1">Commission</p>
                <p className="text-base font-black font-heading commission-amount">
                  {analysis.promo_code ? (
                    <span className="text-green-400">Offerte</span>
                  ) : (
                    formatCurrency(commission.amount)
                  )}
                </p>
              </div>
            </div>
          )}

          {/* Contract info */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            {contract.duration_months && (
              <div>
                <p className="text-[10px] uppercase tracking-[0.1em] font-semibold text-[#f5f5f7]/30 mb-0.5">Duree</p>
                <p className="text-[#f5f5f7]/60">{contract.duration_months} mois</p>
              </div>
            )}
            {contract.remaining_months && (
              <div>
                <p className="text-[10px] uppercase tracking-[0.1em] font-semibold text-[#f5f5f7]/30 mb-0.5">Restants</p>
                <p className="text-[#f5f5f7]/60">{contract.remaining_months} mois</p>
              </div>
            )}
            {contract.total_mileage && (
              <div>
                <p className="text-[10px] uppercase tracking-[0.1em] font-semibold text-[#f5f5f7]/30 mb-0.5">Kilometrage</p>
                <p className="text-[#f5f5f7]/60">{contract.total_mileage.toLocaleString('fr-FR')} km</p>
              </div>
            )}
            {contract.start_date && (
              <div>
                <p className="text-[10px] uppercase tracking-[0.1em] font-semibold text-[#f5f5f7]/30 mb-0.5">Debut</p>
                <p className="text-[#f5f5f7]/60">{contract.start_date}</p>
              </div>
            )}
          </div>

          {/* Event log */}
          {events.length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#f5f5f7]/30 mb-3">Historique</p>
              <div className="space-y-2">
                {events.map((evt, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-green-500/50 shrink-0" />
                    <span className="text-xs text-[#f5f5f7]/40 w-[100px] shrink-0">{formatDate(evt.date)}</span>
                    <span className="text-xs text-[#f5f5f7]/60">{evt.label}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-between">
            <button
              onClick={() => onNavigate(`/analysis/${analysis.id}`)}
              className="flex items-center gap-2 text-xs font-semibold text-green-400 hover:text-green-300 transition-opacity duration-[180ms]"
            >
              Voir l'analyse detaillee
              <ArrowRight size={12} />
            </button>
            {!['accepted', 'in_progress', 'completed'].includes(ps) && (
              confirmDelete ? (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-red-400">Supprimer ce dossier ?</span>
                  <button
                    onClick={async () => {
                      setDeleting(true)
                      try {
                        await onDelete(analysis.id)
                      } finally {
                        setDeleting(false)
                        setConfirmDelete(false)
                      }
                    }}
                    disabled={deleting}
                    className="text-xs font-semibold text-red-400 hover:text-red-300 bg-red-500/10 border border-red-500/30 px-3 py-1 rounded-lg transition-all duration-[180ms] disabled:opacity-40"
                  >
                    {deleting ? <Loader2 size={12} className="animate-spin" /> : 'Oui, supprimer'}
                  </button>
                  <button
                    onClick={() => setConfirmDelete(false)}
                    className="text-xs text-[#f5f5f7]/40 hover:text-[#f5f5f7]/60 px-2 py-1"
                  >
                    Annuler
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setConfirmDelete(true)}
                  className="flex items-center gap-1.5 text-xs text-[#f5f5f7]/25 hover:text-red-400 transition-colors duration-[180ms]"
                >
                  <Trash2 size={12} />
                  Supprimer
                </button>
              )
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default function HistoryPage() {
  const navigate = useNavigate()
  const [analyses, setAnalyses] = useState([])
  const [loading, setLoading] = useState(true)

  const loadAnalyses = useCallback(async () => {
    try {
      const data = await httpRequest(`${API}/analyses`)
      setAnalyses(data)
    } catch (err) {
      console.error(err)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { loadAnalyses() }, [loadAnalyses])

  const handleDelete = useCallback(async (analysisId) => {
    try {
      await httpRequest(`${API}/analyses/${analysisId}`, { method: 'DELETE' })
      toast.success('Dossier supprime')
      setAnalyses(prev => prev.filter(a => a.id !== analysisId))
    } catch (err) {
      toast.error(err instanceof HttpError ? err.message : 'Erreur lors de la suppression')
    }
  }, [])

  const formatCurrency = (val) => {
    if (val == null) return '-'
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(val)
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return '-'
    try {
      return new Date(dateStr).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })
    } catch {
      return dateStr
    }
  }

  return (
    <div className="min-h-screen bg-black" data-testid="history-page">
      <Header />
      <main className="pt-24 pb-16 max-w-5xl mx-auto px-6 md:px-12">
        <div className="mb-10">
          <h1 className="text-3xl sm:text-4xl tracking-tight font-bold text-[#f5f5f7] font-heading">
            Suivi de mes dossiers
          </h1>
          <p className="text-base text-[#f5f5f7]/50 mt-1">
            Suivez l'avancement de chaque demande de reduction
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="spinner" />
          </div>
        ) : analyses.length === 0 ? (
          <div className="bg-[#1c1c1e] border border-white/[0.08] p-16 text-center rounded-2xl" data-testid="history-empty">
            <BarChart3 size={48} className="mx-auto text-[#f5f5f7]/20 mb-4" />
            <p className="text-base text-[#f5f5f7]/50 mb-2">Aucun dossier pour le moment</p>
            <p className="text-sm text-[#f5f5f7]/30 mb-6">Uploadez un contrat ou une facture pour commencer</p>
            <button
              onClick={() => navigate('/dashboard')}
              data-testid="go-to-dashboard-btn"
              className="btn-green inline-flex items-center gap-2 px-6 py-3 text-sm"
            >
              Analyser un document
              <ArrowRight size={16} />
            </button>
          </div>
        ) : (
          <div className="space-y-4" data-testid="history-list">
            {analyses.map((a) => (
              <DossierCard
                key={a.id}
                analysis={a}
                formatCurrency={formatCurrency}
                formatDate={formatDate}
                onNavigate={navigate}
                onDelete={handleDelete}
              />
            ))}
          </div>
        )}
      </main>
    </div>
  )
}
